// Wave 3 — Admin website content management (single tabbed page).
// Owner + Administrator only. All mutations audit-logged.
import React, { useEffect, useState, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import AppShell from "../../../components/AppShell";
import { api, formatApiError } from "../../../lib/api";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import { Save, Trash2, Plus, Globe, Sparkles, MapPin, BarChart3, FileText, Link2, Copy, Megaphone, QrCode, Download, Printer, Mail, Send, UserPlus, MessageSquare } from "lucide-react";

const TABS = ["Content", "Services", "Service Areas", "Portfolio", "Testimonials", "Analytics", "Release Notes"];
const TOUR_KEY = "website_admin_tour_v1";

function copyToClipboard(text, successMsg = "Copied") {
  try {
    navigator.clipboard.writeText(text).then(
      () => toast.success(successMsg),
      () => toast.error("Copy failed — select the URL manually."),
    );
  } catch (_) {
    toast.error("Copy failed — select the URL manually.");
  }
}

function CopyLinkControl({ defaultRef, destinationPath = "/quote", testIdPrefix = "copy-link" }) {
  const [refVal, setRefVal] = useState(defaultRef || "");
  const [qrOpen, setQrOpen] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const clean = (refVal || "").trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  const url = clean ? `${origin}${destinationPath}?ref=${clean}` : `${origin}${destinationPath}`;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testIdPrefix}-control`}>
      <Input
        value={refVal}
        onChange={(e) => setRefVal(e.target.value)}
        placeholder="ref (e.g., linkedin-oct)"
        className="h-7 text-[11px] px-2 max-w-[180px] font-mono"
        data-testid={`${testIdPrefix}-ref`}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => copyToClipboard(url, `Copied ${url}`)}
        className="h-7 px-2 border-erf-borderCream text-[11px]"
        data-testid={`${testIdPrefix}-btn`}
      >
        <Copy className="w-3 h-3 mr-1" /> Copy link
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setQrOpen(true)}
        className="h-7 px-2 border-erf-borderCream text-[11px]"
        title="Show QR code"
        data-testid={`${testIdPrefix}-qr`}
      >
        <QrCode className="w-3 h-3" />
      </Button>
      <QRCodeModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        url={url}
        label={clean || destinationPath.replace("/", "")}
      />
    </div>
  );
}

// ─── R1d.4 — QR Code modal with download PNG/SVG + print ─────────────────
function QRCodeModal({ open, onClose, url, label }) {
  const svgRef = useRef(null);

  function downloadSVG() {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return toast.error("QR code not rendered yet.");
    const s = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([s], { type: "image/svg+xml;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `erf-qr-${label || "campaign"}.svg`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("SVG downloaded");
  }

  function downloadPNG() {
    const svg = svgRef.current?.querySelector("svg");
    if (!svg) return toast.error("QR code not rendered yet.");
    const size = 1024;
    const svgStr = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `erf-qr-${label || "campaign"}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("PNG downloaded (1024×1024)");
      }, "image/png");
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));
  }

  function printQR() {
    const w = window.open("", "erf-qr-print", "width=600,height=800");
    if (!w) return toast.error("Popup blocked — allow popups to print.");
    const svg = svgRef.current?.querySelector("svg");
    const svgStr = svg ? new XMLSerializer().serializeToString(svg) : "";
    w.document.write(`
      <!doctype html><html><head><title>ERF QR — ${label}</title>
      <style>
        @page { margin: 0.5in; }
        body { font-family: 'Inter', sans-serif; text-align: center; padding: 40px; }
        h1 { font-family: 'Playfair Display', serif; color: #B08D57; margin-bottom: 8px; }
        .url { font-family: monospace; font-size: 11px; color: #6B7280; word-break: break-all; margin-top: 16px; }
        .qr { margin: 24px auto; width: 320px; }
        .label { font-size: 14px; color: #1F2937; margin-top: 8px; }
      </style></head>
      <body>
        <h1>ERF ENTERPRISES LLC</h1>
        <div class="label">${label}</div>
        <div class="qr">${svgStr}</div>
        <div class="url">${url}</div>
        <script>window.print(); setTimeout(() => window.close(), 1000);</script>
      </body></html>
    `);
    w.document.close();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-erf-cream" data-testid="qr-code-modal" aria-live="polite">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <QrCode className="w-4 h-4 text-erf-goldHover" /> QR code — {label}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 justify-items-center">
          <div ref={svgRef} className="bg-white p-4 rounded-md border border-erf-borderCream" data-testid="qr-code-svg">
            <QRCodeSVG
              value={url}
              size={256}
              level="M"
              includeMargin={false}
              fgColor="#0F0F10"
              bgColor="#FFFFFF"
              title={`QR code for ${label} — scans to ${url}`}
            />
          </div>
          <div className="font-mono text-[11px] text-erf-dark/70 break-all text-center max-w-full" data-testid="qr-code-url">{url}</div>
          <div className="flex gap-2 flex-wrap justify-center">
            <Button size="sm" variant="outline" onClick={downloadPNG} className="border-erf-borderCream" data-testid="qr-download-png">
              <Download className="w-3.5 h-3.5 mr-1" /> Download PNG (1024×1024)
            </Button>
            <Button size="sm" variant="outline" onClick={downloadSVG} className="border-erf-borderCream" data-testid="qr-download-svg">
              <Download className="w-3.5 h-3.5 mr-1" /> Download SVG
            </Button>
            <Button size="sm" variant="outline" onClick={printQR} className="border-erf-borderCream" data-testid="qr-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="qr-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CONTENT_FIELDS = [
  { key: "tagline", label: "Tagline (footer + hero)", multiline: false },
  { key: "hero_headline", label: "Hero headline (home page)", multiline: false },
  { key: "hero_subheadline", label: "Hero subheadline (home page)", multiline: true },
  { key: "mission_statement", label: "Mission statement (about page)", multiline: true },
  { key: "about_paragraph_1", label: "About paragraph 1", multiline: true },
  { key: "about_paragraph_2", label: "About paragraph 2", multiline: true },
  { key: "value_prop_1_title", label: "Value prop 1 title" },
  { key: "value_prop_1_body", label: "Value prop 1 body", multiline: true },
  { key: "value_prop_2_title", label: "Value prop 2 title" },
  { key: "value_prop_2_body", label: "Value prop 2 body", multiline: true },
  { key: "value_prop_3_title", label: "Value prop 3 title" },
  { key: "value_prop_3_body", label: "Value prop 3 body", multiline: true },
  { key: "service_area_note", label: "Service area note", multiline: true },
  { key: "contact_phone_office", label: "Office phone" },
  { key: "contact_phone_mobile", label: "Mobile phone" },
  { key: "contact_email", label: "Contact email" },
  { key: "license_line", label: "License line (footer)" },
  { key: "footer_copyright", label: "Footer copyright" },
  { key: "cta_headline", label: "CTA band headline (home)" },
  { key: "cta_body", label: "CTA band body (home)", multiline: true },
];

export default function WebsiteAdmin() {
  const [tab, setTab] = useState("Content");
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  useEffect(() => {
    // First-run tour: server-scoped so it doesn't leak across browsers.
    api.get("/tours/progress", { params: { tour_key: TOUR_KEY } })
      .then((r) => {
        const data = r.data;
        if (!data.exists || (!data.completed_at && !data.dismissed_at)) {
          setTourOpen(true);
          setTourStep(data.step || 0);
        }
      })
      .catch(() => {});
  }, []);

  async function advanceTour(nextStep) {
    setTourStep(nextStep);
    try { await api.post("/tours/progress", { tour_key: TOUR_KEY, step: nextStep }); } catch (_) {}
  }
  async function completeTour() {
    setTourOpen(false);
    try { await api.post("/tours/progress", { tour_key: TOUR_KEY, completed: true }); } catch (_) {}
  }
  async function dismissTour() {
    setTourOpen(false);
    try { await api.post("/tours/progress", { tour_key: TOUR_KEY, dismissed: true }); } catch (_) {}
  }

  return (
    <AppShell
      title="Website Content"
      subtitle="Public site at / — content, services, service areas, portfolio, testimonials, and analytics."
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setTourStep(0); setTourOpen(true); }}
          className="border-erf-borderCream"
          data-testid="website-tour-relaunch"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1" /> Take the tour
        </Button>
      }
    >
      <div className="flex gap-1.5 mb-5 flex-wrap" data-testid="website-admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            className={`px-3 py-1.5 text-sm font-nav rounded-md border ${tab === t ? "bg-erf-dark text-erf-cream border-erf-dark" : "bg-white border-erf-borderCream text-erf-dark/70 hover:bg-erf-creamSecondary"}`}
            onClick={() => setTab(t)}
            data-testid={`website-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {t}
          </button>
        ))}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-xs font-mono text-erf-goldHover inline-flex items-center gap-1 self-center hover:underline"
          data-testid="website-preview-link"
        >
          <Globe className="w-3 h-3" /> Open public site
        </a>
      </div>
      {tab === "Content" && <ContentTab />}
      {tab === "Services" && <ServicesTab />}
      {tab === "Service Areas" && <ServiceAreasTab />}
      {tab === "Portfolio" && <PortfolioTab />}
      {tab === "Testimonials" && <TestimonialsTab />}
      {tab === "Analytics" && <AnalyticsTab />}
      {tab === "Release Notes" && <ReleaseNotesTab />}
      <TourDialog
        open={tourOpen}
        step={tourStep}
        onNext={advanceTour}
        onComplete={completeTour}
        onDismiss={dismissTour}
      />
    </AppShell>
  );
}

function ContentTab() {
  const [content, setContent] = useState({});
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    api.get("/admin/website/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const upd = (k, v) => {
    setContent((prev) => ({ ...prev, [k]: v }));
    setDirty((prev) => ({ ...prev, [k]: true }));
  };

  async function save(k) {
    try {
      await api.put(`/admin/website/content/${k}`, { key: k, value: content[k] || "" });
      setDirty((prev) => { const n = { ...prev }; delete n[k]; return n; });
      toast.success(`Saved ${k}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="grid gap-3 max-w-3xl" data-testid="website-content-tab">
      {CONTENT_FIELDS.map((f) => (
        <Card key={f.key} className="border-erf-borderCream">
          <CardContent className="p-4">
            <div className="flex items-end justify-between gap-3 mb-2">
              <Label className="text-xs font-mono uppercase tracking-widest">{f.label}</Label>
              <Button size="sm" onClick={() => save(f.key)} disabled={!dirty[f.key]}
                className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                data-testid={`content-save-${f.key}`}>
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
            {f.multiline ? (
              <textarea
                value={content[f.key] || ""}
                onChange={(e) => upd(f.key, e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav"
                data-testid={`content-input-${f.key}`}
              />
            ) : (
              <Input value={content[f.key] || ""} onChange={(e) => upd(f.key, e.target.value)} data-testid={`content-input-${f.key}`} />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ServicesTab() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", short_description: "", long_description: "", published: true });

  useEffect(() => {
    api.get("/admin/website/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  function edit(s) {
    setEditing(s.slug);
    setForm({
      name: s.name, short_description: s.short_description,
      long_description: s.long_description || "", published: s.published !== false,
    });
  }

  async function save() {
    try {
      const r = await api.patch(`/admin/website/services/${editing}`, form);
      setServices((prev) => prev.map((s) => s.slug === editing ? r.data : s));
      setEditing(null);
      toast.success("Service updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="grid gap-2 max-w-3xl" data-testid="website-services-tab">
      {services.map((s) => (
        <Card key={s.slug} className="border-erf-borderCream" data-testid={`service-row-${s.slug}`}>
          <CardContent className="p-4">
            {editing === s.slug ? (
              <div className="grid gap-3">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`service-name-${s.slug}`} />
                <Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid={`service-short-${s.slug}`} />
                <textarea rows={4} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })}
                  placeholder="Longer description shown on the service detail page"
                  className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav"
                  data-testid={`service-long-${s.slug}`} />
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid={`service-published-${s.slug}`} />
                  Published on public site
                </label>
                <div className="flex gap-2">
                  <Button size="sm" onClick={save} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid={`service-save-${s.slug}`}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">{s.slug}</div>
                  <div className="font-display font-semibold text-erf-dark">{s.name}</div>
                  <div className="text-xs font-nav text-erf-dark/60 mt-1">{s.short_description}</div>
                  <div className="mt-2">
                    <CopyLinkControl defaultRef={s.slug} destinationPath="/quote" testIdPrefix={`service-copy-${s.slug}`} />
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => edit(s)} className="border-erf-borderCream" data-testid={`service-edit-${s.slug}`}>Edit</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// R1d.8 — Portfolio Admin tab: Owner-only draft → publish → unpublish workflow.
// Every state transition is server-authoritative; permission-denied surfaces as
// a truthful toast. Photo upload requires alt_text; upload button is disabled
// until every staged photo has one.
function StatusPill({ status }) {
  const map = {
    draft: { bg: "bg-slate-100", fg: "text-slate-600", label: "Draft" },
    published: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "Published" },
    unpublished: { bg: "bg-amber-50", fg: "text-amber-700", label: "Unpublished" },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${s.bg} ${s.fg}`}
      role="status"
      data-testid={`portfolio-status-pill-${status}`}
    >
      {s.label}
    </span>
  );
}

function ConfirmModal({ open, title, message, confirmLabel, onConfirm, onCancel, danger, testId }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 id={`${testId}-title`} className="text-lg font-display font-semibold text-erf-dark mb-2">{title}</h3>
        <p className="text-sm text-erf-dark/70 mb-4 font-nav">{message}</p>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            className={danger ? "bg-red-600 text-white hover:bg-red-700" : "bg-erf-gold text-erf-dark hover:bg-erf-goldHover"}
            data-testid={`${testId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PortfolioTab() {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // project row being edited
  const [saving, setSaving] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);
  const [unpublishTarget, setUnpublishTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  // Staged photos: {file, alt_text, uploading, error, document_id, original_document_id, processing_notes, ai_draft}
  const [stagedPhotos, setStagedPhotos] = useState([]);
  // Workstream C — staged videos: same shape plus poster_document_id, duration_sec, transcript.
  const [stagedVideos, setStagedVideos] = useState([]);
  // Workstream B — inline AI-analysis state so the button can show a spinner.
  const [aiPending, setAiPending] = useState({}); // { [document_id]: bool }
  const emptyForm = {
    project_name: "", service_slug: "", city: "", state: "",
    summary: "", completion_date: "", duration: "", budget_bracket: "",
    client_attribution: "", client_consent_verified: false,
    photos: [], videos: [],
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/admin/website/portfolio"),
        api.get("/admin/website/services"),
      ]);
      setProjects(p.data);
      setServices(s.data);
      setPermissionDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setPermissionDenied(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  function resetForm() {
    setForm(emptyForm);
    setStagedPhotos([]);
    setStagedVideos([]);
    setEditing(null);
    setShowForm(false);
  }

  const canSave = form.project_name.trim() && form.service_slug && form.city.trim() &&
    form.state.trim().length === 2 && form.summary.trim() &&
    stagedPhotos.every((sp) => sp.document_id && (sp.alt_text || "").trim().length > 0) &&
    stagedVideos.every((sv) => sv.document_id && (sv.alt_text || "").trim().length > 0);

  async function uploadStagedPhoto(idx) {
    const sp = stagedPhotos[idx];
    if (!sp || sp.document_id || !(sp.alt_text || "").trim()) return;
    const fd = new FormData();
    fd.append("file", sp.file);
    fd.append("alt_text", sp.alt_text.trim());
    fd.append("kind", "photo");
    setStagedPhotos((prev) => prev.map((x, i) => i === idx ? { ...x, uploading: true, error: null } : x));
    try {
      // Workstream B — new endpoint stores immutable original + deterministic derivative.
      const r = await api.post("/admin/website/portfolio/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStagedPhotos((prev) => prev.map((x, i) => i === idx ? {
        ...x, uploading: false, document_id: r.data.document_id,
        original_document_id: r.data.original_document_id,
        processing_notes: r.data.processing_notes || [],
        width: r.data.width, height: r.data.height,
      } : x));
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setStagedPhotos((prev) => prev.map((x, i) => i === idx ? { ...x, uploading: false, error: msg } : x));
    }
  }

  async function uploadStagedVideo(idx) {
    const sv = stagedVideos[idx];
    if (!sv || sv.document_id || !(sv.alt_text || "").trim()) return;
    const fd = new FormData();
    fd.append("file", sv.file);
    fd.append("alt_text", sv.alt_text.trim());
    fd.append("kind", "video");
    setStagedVideos((prev) => prev.map((x, i) => i === idx ? { ...x, uploading: true, error: null } : x));
    try {
      const r = await api.post("/admin/website/portfolio/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStagedVideos((prev) => prev.map((x, i) => i === idx ? {
        ...x, uploading: false, document_id: r.data.document_id,
        original_document_id: r.data.original_document_id,
        poster_document_id: r.data.poster_document_id,
        processing_notes: r.data.processing_notes || [],
        width: r.data.width, height: r.data.height,
        duration_sec: r.data.duration_sec,
      } : x));
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setStagedVideos((prev) => prev.map((x, i) => i === idx ? { ...x, uploading: false, error: msg } : x));
    }
  }

  function stagePhotos(fileList) {
    const files = Array.from(fileList || []);
    const additions = files.map((file) => ({
      file, alt_text: "", uploading: false, error: null, document_id: null,
      original_document_id: null, processing_notes: [], ai_draft: null,
      preview_url: URL.createObjectURL(file),
    }));
    setStagedPhotos((prev) => [...prev, ...additions]);
  }

  function stageVideos(fileList) {
    const files = Array.from(fileList || []);
    const additions = files.map((file) => ({
      file, alt_text: "", uploading: false, error: null, document_id: null,
      original_document_id: null, poster_document_id: null,
      processing_notes: [], ai_draft: null, transcript: null,
      duration_sec: null,
      preview_url: URL.createObjectURL(file),
    }));
    setStagedVideos((prev) => [...prev, ...additions]);
  }

  async function runAiAnalysis(kind, idx) {
    const arr = kind === "photo" ? stagedPhotos : stagedVideos;
    const setter = kind === "photo" ? setStagedPhotos : setStagedVideos;
    const item = arr[idx];
    if (!item || !item.document_id) {
      toast.error("Upload the media first, then request an AI draft.");
      return;
    }
    setAiPending((p) => ({ ...p, [item.document_id]: true }));
    try {
      const r = await api.post(`/admin/website/portfolio/media/${item.document_id}/ai-analyze`);
      setter((prev) => prev.map((x, i) => i === idx ? {
        ...x, ai_draft: r.data.ai_draft || null, transcript: r.data.transcript || null,
      } : x));
      if (r.data.ai_draft?.error) {
        toast.warning(`AI draft returned an error: ${r.data.ai_draft.error}`);
      } else {
        toast.success("AI draft ready — Founder approval required to publish.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAiPending((p) => { const n = { ...p }; delete n[item.document_id]; return n; });
    }
  }

  function applyAiTitle(kind, idx) {
    const arr = kind === "photo" ? stagedPhotos : stagedVideos;
    const item = arr[idx];
    const title = item?.ai_draft?.draft_title;
    if (!title) return;
    if (!form.project_name.trim()) {
      setForm((f) => ({ ...f, project_name: title }));
      toast.success("Draft title applied to project name (edit before saving).");
    } else {
      toast.info("Project name already set. Copy manually if you want to replace it.");
    }
  }

  function applyAiSummary(kind, idx) {
    const arr = kind === "photo" ? stagedPhotos : stagedVideos;
    const item = arr[idx];
    const s = item?.ai_draft?.draft_summary;
    if (!s) return;
    if (!form.summary.trim()) {
      setForm((f) => ({ ...f, summary: s }));
      toast.success("Draft summary applied (edit before saving).");
    } else {
      toast.info("Summary already set. Copy manually if you want to replace it.");
    }
  }

  async function save() {
    if (!canSave) return;
    setSaving(true);
    try {
      const photos = stagedPhotos
        .filter((sp) => sp.document_id)
        .map((sp) => ({
          document_id: sp.document_id, alt_text: sp.alt_text.trim(),
          original_document_id: sp.original_document_id || null,
          processing_notes: sp.processing_notes || [],
          ai_draft: sp.ai_draft || null,
        }));
      const videos = stagedVideos
        .filter((sv) => sv.document_id)
        .map((sv) => ({
          document_id: sv.document_id, alt_text: sv.alt_text.trim(),
          original_document_id: sv.original_document_id || null,
          poster_document_id: sv.poster_document_id || null,
          processing_notes: sv.processing_notes || [],
          duration_sec: sv.duration_sec || null,
          ai_draft: sv.ai_draft || null,
          transcript: sv.transcript || null,
        }));
      const payload = { ...form, photos, videos };
      if (editing) {
        const r = await api.patch(`/admin/website/portfolio/${editing.id}`, payload);
        setProjects((prev) => prev.map((p) => p.id === editing.id ? r.data : p));
        toast.success("Draft updated");
      } else {
        const r = await api.post("/admin/website/portfolio", payload);
        setProjects((prev) => [r.data, ...prev]);
        toast.success("Draft created");
      }
      resetForm();
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) toast.error("Owner permission required.");
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  }

  async function publish(id) {
    try {
      const r = await api.post(`/admin/website/portfolio/${id}/publish`);
      setProjects((prev) => prev.map((p) => p.id === id ? { ...p, status: "published", published: true, published_at: r.data.published_at } : p));
      setPublishTarget(null);
      toast.success("Project published to /portfolio.");
    } catch (e) {
      setPublishTarget(null);
      if (e.response?.status === 403) toast.error("Owner permission required to publish.");
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  async function unpublish(id) {
    try {
      await api.post(`/admin/website/portfolio/${id}/unpublish`);
      setProjects((prev) => prev.map((p) => p.id === id ? { ...p, status: "unpublished", published: false } : p));
      setUnpublishTarget(null);
      toast.success("Project unpublished.");
    } catch (e) {
      setUnpublishTarget(null);
      if (e.response?.status === 403) toast.error("Owner permission required to unpublish.");
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  async function remove(id) {
    try {
      await api.delete(`/admin/website/portfolio/${id}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setDeleteTarget(null);
      toast.success("Deleted");
    } catch (e) {
      setDeleteTarget(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  function beginEdit(p) {
    setEditing(p);
    setShowForm(true);
    setForm({
      project_name: p.project_name || "", service_slug: p.service_slug || "",
      city: p.city || "", state: p.state || "",
      summary: p.summary || "", completion_date: p.completion_date || "",
      duration: p.duration || "", budget_bracket: p.budget_bracket || "",
      client_attribution: p.client_attribution || "",
      client_consent_verified: !!p.client_consent_verified,
      photos: p.photos || [], videos: p.videos || [],
    });
    setStagedPhotos([]);
    setStagedVideos([]);
  }

  if (permissionDenied) {
    return (
      <div className="max-w-2xl" data-testid="portfolio-permission-denied">
        <Card className="border-red-200">
          <CardContent className="p-8 text-center">
            <div className="text-sm font-mono uppercase tracking-widest text-red-600 mb-1">Owner permission required</div>
            <p className="text-sm text-erf-dark/70 font-nav">The portfolio administration workspace is Owner-only. Sign in as an Owner account to manage projects.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="website-portfolio-tab">
      <div className="flex justify-between mb-3">
        <div className="text-sm font-nav text-erf-dark/70">
          Real completed projects only. No placeholders. {projects.length} in library.
        </div>
        <Button
          size="sm"
          onClick={() => { setEditing(null); setForm(emptyForm); setStagedPhotos([]); setShowForm(true); }}
          className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
          data-testid="portfolio-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Create draft
        </Button>
      </div>

      {showForm && (
        <Card className="border-erf-borderCream mb-3">
          <CardContent className="p-5 grid gap-3">
            <div className="text-xs font-mono uppercase tracking-widest text-erf-goldHover">
              {editing ? `Edit: ${editing.project_name}` : "New draft"}
            </div>
            <label className="text-xs font-nav font-semibold text-erf-dark/70">Project name
              <Input placeholder="Project name" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="portfolio-name" />
            </label>
            <label className="text-xs font-nav font-semibold text-erf-dark/70">Service category
              <select value={form.service_slug} onChange={(e) => setForm({ ...form, service_slug: e.target.value })}
                className="w-full h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white" data-testid="portfolio-service">
                <option value="">— Select a service —</option>
                {services.map((s) => (<option key={s.slug} value={s.slug}>{s.name}</option>))}
              </select>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="portfolio-city" />
              <Input placeholder="State (2 letters)" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} data-testid="portfolio-state" />
            </div>
            <textarea rows={3} placeholder="Project summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav" data-testid="portfolio-summary" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input placeholder="Completion date (opt)" type="date" value={form.completion_date} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} data-testid="portfolio-date" />
              <Input placeholder="Duration (opt)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="portfolio-duration" />
              <Input placeholder="Budget bracket (opt)" value={form.budget_bracket} onChange={(e) => setForm({ ...form, budget_bracket: e.target.value })} data-testid="portfolio-budget" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Client attribution (opt)" value={form.client_attribution} onChange={(e) => setForm({ ...form, client_attribution: e.target.value })} data-testid="portfolio-client" />
              <label className="text-sm flex items-center gap-2 font-nav">
                <input type="checkbox" checked={form.client_consent_verified} onChange={(e) => setForm({ ...form, client_consent_verified: e.target.checked })} data-testid="portfolio-consent" />
                Client consent verified
              </label>
            </div>

            {/* R1d.8 + Workstream B — Photo staging with immutable original + AI draft flow. */}
            <div className="border-2 border-dashed border-erf-borderCream rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-nav font-semibold text-erf-dark/70">Project photos (real, honest)</div>
                <label
                  className="text-xs font-mono uppercase tracking-widest text-erf-goldHover cursor-pointer hover:text-erf-gold"
                  data-testid="portfolio-photo-picker"
                >
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                    onChange={(e) => { stagePhotos(e.target.files); e.target.value = ""; }}
                    data-testid="portfolio-photo-input" />
                  + Add photos
                </label>
              </div>
              {stagedPhotos.length === 0 ? (
                <div className="text-xs text-erf-dark/50 font-nav">
                  No photos staged. JPG / PNG / WEBP up to 15 MB each. Immutable original is preserved privately; a deterministically re-encoded derivative (EXIF and GPS stripped) is what the public site serves. Alt text is required.
                </div>
              ) : (
                <div className="grid gap-2">
                  {stagedPhotos.map((sp, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 bg-erf-cream/40 rounded" data-testid={`portfolio-staged-photo-${idx}`}>
                      <img src={sp.preview_url} alt={sp.alt_text || "staged photo preview"} className="w-16 h-16 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono text-erf-dark/60 truncate">{sp.file.name} · {(sp.file.size / 1024 / 1024).toFixed(2)} MB</div>
                        <Input
                          placeholder="Alt text (required — describe the photo for screen readers)"
                          value={sp.alt_text}
                          onChange={(e) => setStagedPhotos((prev) => prev.map((x, i) => i === idx ? { ...x, alt_text: e.target.value } : x))}
                          data-testid={`portfolio-photo-alt-${idx}`}
                        />
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {!sp.document_id && (
                            <Button size="sm" onClick={() => uploadStagedPhoto(idx)} disabled={!sp.alt_text.trim() || sp.uploading}
                              className="bg-erf-dark text-white hover:bg-erf-dark/90"
                              data-testid={`portfolio-photo-upload-${idx}`}>
                              {sp.uploading ? "Uploading…" : "Upload"}
                            </Button>
                          )}
                          {sp.document_id && (
                            <>
                              <span className="text-[11px] font-mono text-emerald-700" data-testid={`portfolio-photo-uploaded-${idx}`}>✓ uploaded</span>
                              <Button size="sm" variant="outline" onClick={() => runAiAnalysis("photo", idx)}
                                disabled={!!aiPending[sp.document_id]}
                                className="border-erf-borderCream text-[11px] h-7 px-2"
                                data-testid={`portfolio-photo-ai-${idx}`}>
                                <Sparkles className="w-3 h-3 mr-1" />{aiPending[sp.document_id] ? "Analyzing…" : (sp.ai_draft ? "Re-run AI draft" : "Get AI draft")}
                              </Button>
                            </>
                          )}
                          {sp.error && (
                            <span className="text-[11px] font-mono text-red-600" role="alert" data-testid={`portfolio-photo-error-${idx}`}>{sp.error}</span>
                          )}
                          <button type="button" onClick={() => setStagedPhotos((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-[11px] text-red-500 hover:text-red-700 ml-auto" data-testid={`portfolio-photo-remove-${idx}`}>
                            Remove
                          </button>
                        </div>
                        {sp.processing_notes && sp.processing_notes.length > 0 && (
                          <div className="text-[10px] font-mono text-erf-dark/50 mt-1" data-testid={`portfolio-photo-notes-${idx}`}>
                            Processing: {sp.processing_notes.join(" · ")}
                          </div>
                        )}
                        {sp.ai_draft && (
                          <div className="mt-2 p-2 border border-amber-200 bg-amber-50/70 rounded text-[11px]" role="note" data-testid={`portfolio-photo-ai-draft-${idx}`}>
                            <div className="font-mono uppercase tracking-widest text-amber-800 text-[9px] mb-1">
                              AI Draft — Founder approval required · model {sp.ai_draft.model || "gpt-5.4"}
                            </div>
                            {sp.ai_draft.error ? (
                              <div className="text-red-600 font-mono">Error: {sp.ai_draft.error}</div>
                            ) : (
                              <>
                                {sp.ai_draft.draft_title && (
                                  <div className="mb-1">
                                    <span className="font-semibold text-erf-dark">Suggested title:</span> {sp.ai_draft.draft_title}
                                    <button type="button" onClick={() => applyAiTitle("photo", idx)}
                                      className="ml-2 text-erf-goldHover underline text-[10px]"
                                      data-testid={`portfolio-photo-apply-title-${idx}`}>Apply to project name</button>
                                  </div>
                                )}
                                {sp.ai_draft.draft_summary && (
                                  <div className="mb-1">
                                    <span className="font-semibold text-erf-dark">Suggested summary:</span> {sp.ai_draft.draft_summary}
                                    <button type="button" onClick={() => applyAiSummary("photo", idx)}
                                      className="ml-2 text-erf-goldHover underline text-[10px]"
                                      data-testid={`portfolio-photo-apply-summary-${idx}`}>Apply to summary</button>
                                  </div>
                                )}
                                {(sp.ai_draft.alt_text_candidates || []).length > 0 && (
                                  <div>
                                    <span className="font-semibold text-erf-dark">Alt-text candidates:</span>
                                    <ul className="list-disc ml-5 mt-0.5">
                                      {sp.ai_draft.alt_text_candidates.map((c, i2) => (
                                        <li key={i2}>
                                          {c}
                                          <button type="button" onClick={() => setStagedPhotos((prev) => prev.map((x, i3) => i3 === idx ? { ...x, alt_text: c } : x))}
                                            className="ml-2 text-erf-goldHover underline text-[10px]"
                                            data-testid={`portfolio-photo-apply-alt-${idx}-${i2}`}>Use</button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Workstream C — Video staging (MP4 / MOV / WebM), FFmpeg re-encode, Whisper transcript. */}
            <div className="border-2 border-dashed border-erf-borderCream rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-nav font-semibold text-erf-dark/70">Project videos (real footage)</div>
                <label
                  className="text-xs font-mono uppercase tracking-widest text-erf-goldHover cursor-pointer hover:text-erf-gold"
                  data-testid="portfolio-video-picker"
                >
                  <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.m4v,.webm" multiple hidden
                    onChange={(e) => { stageVideos(e.target.files); e.target.value = ""; }}
                    data-testid="portfolio-video-input" />
                  + Add videos
                </label>
              </div>
              {stagedVideos.length === 0 ? (
                <div className="text-xs text-erf-dark/50 font-nav">
                  No videos staged. MP4 / MOV / WebM up to 500 MB each. Immutable original preserved privately; a deterministic H.264 derivative (metadata stripped, auto-oriented) is what the public site serves. AI-generated captions and Whisper transcripts are drafts that require Founder approval.
                </div>
              ) : (
                <div className="grid gap-2">
                  {stagedVideos.map((sv, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 bg-erf-cream/40 rounded" data-testid={`portfolio-staged-video-${idx}`}>
                      <video src={sv.preview_url} className="w-24 h-16 object-cover rounded bg-black" muted preload="metadata" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono text-erf-dark/60 truncate">
                          {sv.file.name} · {(sv.file.size / 1024 / 1024).toFixed(2)} MB
                          {sv.duration_sec ? ` · ${sv.duration_sec.toFixed(1)}s` : ""}
                        </div>
                        <Input
                          placeholder="Alt text (required — describe the video for screen readers)"
                          value={sv.alt_text}
                          onChange={(e) => setStagedVideos((prev) => prev.map((x, i) => i === idx ? { ...x, alt_text: e.target.value } : x))}
                          data-testid={`portfolio-video-alt-${idx}`}
                        />
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {!sv.document_id && (
                            <Button size="sm" onClick={() => uploadStagedVideo(idx)} disabled={!sv.alt_text.trim() || sv.uploading}
                              className="bg-erf-dark text-white hover:bg-erf-dark/90"
                              data-testid={`portfolio-video-upload-${idx}`}>
                              {sv.uploading ? "Processing…" : "Upload"}
                            </Button>
                          )}
                          {sv.document_id && (
                            <>
                              <span className="text-[11px] font-mono text-emerald-700" data-testid={`portfolio-video-uploaded-${idx}`}>✓ uploaded</span>
                              <Button size="sm" variant="outline" onClick={() => runAiAnalysis("video", idx)}
                                disabled={!!aiPending[sv.document_id]}
                                className="border-erf-borderCream text-[11px] h-7 px-2"
                                data-testid={`portfolio-video-ai-${idx}`}>
                                <Sparkles className="w-3 h-3 mr-1" />{aiPending[sv.document_id] ? "Analyzing…" : (sv.ai_draft ? "Re-run AI draft" : "Get AI draft + transcript")}
                              </Button>
                            </>
                          )}
                          {sv.error && (
                            <span className="text-[11px] font-mono text-red-600" role="alert" data-testid={`portfolio-video-error-${idx}`}>{sv.error}</span>
                          )}
                          <button type="button" onClick={() => setStagedVideos((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-[11px] text-red-500 hover:text-red-700 ml-auto" data-testid={`portfolio-video-remove-${idx}`}>
                            Remove
                          </button>
                        </div>
                        {sv.processing_notes && sv.processing_notes.length > 0 && (
                          <div className="text-[10px] font-mono text-erf-dark/50 mt-1" data-testid={`portfolio-video-notes-${idx}`}>
                            Processing: {sv.processing_notes.join(" · ")}
                          </div>
                        )}
                        {sv.ai_draft && (
                          <div className="mt-2 p-2 border border-amber-200 bg-amber-50/70 rounded text-[11px]" role="note" data-testid={`portfolio-video-ai-draft-${idx}`}>
                            <div className="font-mono uppercase tracking-widest text-amber-800 text-[9px] mb-1">
                              AI Draft — Founder approval required · model {sv.ai_draft.model || "gpt-5.4"}
                            </div>
                            {sv.ai_draft.error ? (
                              <div className="text-red-600 font-mono">Error: {sv.ai_draft.error}</div>
                            ) : (
                              <>
                                {sv.ai_draft.draft_title && (
                                  <div className="mb-1">
                                    <span className="font-semibold text-erf-dark">Suggested title:</span> {sv.ai_draft.draft_title}
                                    <button type="button" onClick={() => applyAiTitle("video", idx)}
                                      className="ml-2 text-erf-goldHover underline text-[10px]"
                                      data-testid={`portfolio-video-apply-title-${idx}`}>Apply</button>
                                  </div>
                                )}
                                {sv.ai_draft.draft_summary && (
                                  <div className="mb-1">
                                    <span className="font-semibold text-erf-dark">Suggested summary:</span> {sv.ai_draft.draft_summary}
                                    <button type="button" onClick={() => applyAiSummary("video", idx)}
                                      className="ml-2 text-erf-goldHover underline text-[10px]"
                                      data-testid={`portfolio-video-apply-summary-${idx}`}>Apply</button>
                                  </div>
                                )}
                                {(sv.ai_draft.alt_text_candidates || []).length > 0 && (
                                  <div>
                                    <span className="font-semibold text-erf-dark">Alt-text candidates:</span>
                                    <ul className="list-disc ml-5 mt-0.5">
                                      {sv.ai_draft.alt_text_candidates.map((c, i2) => (
                                        <li key={i2}>
                                          {c}
                                          <button type="button" onClick={() => setStagedVideos((prev) => prev.map((x, i3) => i3 === idx ? { ...x, alt_text: c } : x))}
                                            className="ml-2 text-erf-goldHover underline text-[10px]"
                                            data-testid={`portfolio-video-apply-alt-${idx}-${i2}`}>Use</button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {sv.transcript && (
                          <div className="mt-2 p-2 border border-amber-200 bg-amber-50/70 rounded text-[11px]" role="note" data-testid={`portfolio-video-transcript-${idx}`}>
                            <div className="font-mono uppercase tracking-widest text-amber-800 text-[9px] mb-1">
                              Whisper transcript — Founder approval required · {sv.transcript.language ? `lang ${sv.transcript.language}` : "auto"}
                            </div>
                            {sv.transcript.error ? (
                              <div className="text-erf-dark/60 font-mono text-[10px]">Transcript unavailable: {sv.transcript.error}</div>
                            ) : (
                              <div className="text-erf-dark/80 whitespace-pre-line">{sv.transcript.transcript_text || "(silent clip — no speech detected)"}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2" aria-live="polite">
              <Button
                size="sm"
                onClick={save}
                disabled={!canSave || saving}
                className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="portfolio-save"
              >
                {saving ? "Saving…" : (editing ? "Save changes" : "Save draft")}
              </Button>
              <Button size="sm" variant="outline" onClick={resetForm} data-testid="portfolio-cancel">Cancel</Button>
              {!canSave && (
                <span className="text-[11px] font-mono text-erf-dark/50 self-center" role="status">
                  All required fields + alt-text on every media item needed to save.
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Card className="border-erf-borderCream">
          <CardContent className="p-6 text-center text-sm font-nav text-erf-dark/50" data-testid="portfolio-loading">
            Loading portfolio…
          </CardContent>
        </Card>
      ) : projects.length === 0 ? (
        <Card className="border-erf-borderCream" data-testid="portfolio-empty">
          <CardContent className="p-8 text-center text-erf-dark/60 font-nav text-sm">
            No portfolio projects yet — click <strong>Create draft</strong> to add your first real ERF project.
            Drafts stay private until you publish them.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {projects.map((p) => {
            const status = p.status || (p.published ? "published" : "draft");
            return (
              <Card key={p.id} className="border-erf-borderCream" data-testid={`portfolio-row-${p.id}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">{p.service_slug}</div>
                      <StatusPill status={status} />
                    </div>
                    <div className="font-display font-semibold text-erf-dark">{p.project_name}</div>
                    <div className="text-xs font-nav text-erf-dark/60">
                      {p.city}, {p.state} · {p.completion_date || "no date"}
                      {(p.photos && p.photos.length > 0) && <span> · {p.photos.length} photo{p.photos.length === 1 ? "" : "s"}</span>}
                      {(p.videos && p.videos.length > 0) && <span> · {p.videos.length} video{p.videos.length === 1 ? "" : "s"}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setPreviewRow(p)} data-testid={`portfolio-preview-${p.id}`}>Preview</Button>
                    <Button size="sm" variant="outline" onClick={() => beginEdit(p)} data-testid={`portfolio-edit-${p.id}`}>Edit</Button>
                    {status !== "published" && (
                      <Button size="sm" onClick={() => setPublishTarget(p)} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`portfolio-publish-${p.id}`}>Publish</Button>
                    )}
                    {status === "published" && (
                      <Button size="sm" variant="outline" onClick={() => setUnpublishTarget(p)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`portfolio-unpublish-${p.id}`}>Unpublish</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setDeleteTarget(p)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`portfolio-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={!!publishTarget}
        title="Publish project?"
        message={`"${publishTarget?.project_name || ""}" will become visible on the public /portfolio page. Confirm?`}
        confirmLabel="Publish"
        onConfirm={() => publish(publishTarget.id)}
        onCancel={() => setPublishTarget(null)}
        testId="portfolio-publish-modal"
      />
      <ConfirmModal
        open={!!unpublishTarget}
        title="Unpublish project?"
        message={`"${unpublishTarget?.project_name || ""}" will be removed from the public /portfolio page. It remains saved as a draft. Confirm?`}
        confirmLabel="Unpublish"
        onConfirm={() => unpublish(unpublishTarget.id)}
        onCancel={() => setUnpublishTarget(null)}
        testId="portfolio-unpublish-modal"
      />
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete draft?"
        message={`Permanently delete "${deleteTarget?.project_name || ""}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={() => remove(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        testId="portfolio-delete-modal"
      />

      {previewRow && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          role="dialog" aria-modal="true" aria-labelledby="portfolio-preview-title"
          onClick={() => setPreviewRow(null)}
          data-testid="portfolio-preview-drawer">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <StatusPill status={previewRow.status || (previewRow.published ? "published" : "draft")} />
              <button type="button" onClick={() => setPreviewRow(null)} className="text-erf-dark/50 hover:text-erf-dark text-sm" aria-label="Close preview">✕</button>
            </div>
            <h3 id="portfolio-preview-title" className="text-xl font-display font-semibold text-erf-dark">{previewRow.project_name}</h3>
            <div className="text-xs font-nav text-erf-dark/60 mb-3">{previewRow.city}, {previewRow.state} · {previewRow.completion_date || "no date"}</div>
            <p className="text-sm font-nav text-erf-dark/80 whitespace-pre-line">{previewRow.summary}</p>
            {(previewRow.photos && previewRow.photos.length > 0) && (
              <div className="mt-3 text-xs font-mono text-erf-dark/50">
                {previewRow.photos.length} photo{previewRow.photos.length === 1 ? "" : "s"} attached
              </div>
            )}
            {(previewRow.videos && previewRow.videos.length > 0) && (
              <div className="mt-1 text-xs font-mono text-erf-dark/50">
                {previewRow.videos.length} video{previewRow.videos.length === 1 ? "" : "s"} attached
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ quote_text: "", author_name: "", author_role: "", project_slug: "", consent_verified: false });

  useEffect(() => {
    api.get("/admin/website/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  async function create() {
    if (!form.consent_verified) { toast.error("Consent must be verified to save a testimonial."); return; }
    try {
      const r = await api.post("/admin/website/testimonials", form);
      setItems((prev) => [r.data, ...prev]);
      setShowForm(false);
      setForm({ quote_text: "", author_name: "", author_role: "", project_slug: "", consent_verified: false });
      toast.success("Testimonial added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/admin/website/testimonials/${id}`);
      setItems((prev) => prev.filter((t) => t.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="max-w-3xl" data-testid="website-testimonials-tab">
      <div className="flex justify-between mb-3">
        <div className="text-sm font-nav text-erf-dark/70">Real testimonials only, with verified consent. {items.length} on file.</div>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="testimonial-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Add testimonial
        </Button>
      </div>
      {showForm && (
        <Card className="border-erf-borderCream mb-3">
          <CardContent className="p-5 grid gap-3">
            <textarea rows={4} placeholder="Quote text" value={form.quote_text} onChange={(e) => setForm({ ...form, quote_text: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav" data-testid="testimonial-quote" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Author name" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="testimonial-name" />
              <Input placeholder="Author role (opt)" value={form.author_role} onChange={(e) => setForm({ ...form, author_role: e.target.value })} data-testid="testimonial-role" />
            </div>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.consent_verified} onChange={(e) => setForm({ ...form, consent_verified: e.target.checked })} data-testid="testimonial-consent" />
              I have written consent from this person to display their testimonial publicly
            </label>
            <div className="flex gap-2">
              <Button size="sm" onClick={create} disabled={!form.consent_verified} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="testimonial-save">Save</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}
      {items.length === 0 ? (
        <Card className="border-erf-borderCream">
          <CardContent className="p-8 text-center text-erf-dark/60 font-nav text-sm">
            No testimonials yet. Add a real one with verified consent to populate the home page.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {items.map((t) => (
            <Card key={t.id} className="border-erf-borderCream">
              <CardContent className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="italic text-sm text-erf-dark/80">"{t.quote_text}"</div>
                  <div className="mt-2 text-xs font-mono text-erf-dark/70">— {t.author_name}{t.author_role ? `, ${t.author_role}` : ""}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(t.id)} className="border-red-200 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Wave 3.2b — Service Areas admin tab ────────────────────────────────
function ServiceAreasTab() {
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = () => ({
    state_slug: "", state_code: "", state_name: "",
    tier: "primary", order: 100,
    coverage_summary: "", counties: [], featured_services: [],
    published: true,
  });
  const [form, setForm] = useState(emptyForm());

  useEffect(() => { load(); }, []);
  function load() {
    api.get("/admin/website/service-areas").then((r) => setAreas(r.data)).catch(() => {});
  }

  function startEdit(a) {
    setEditing(a.state_slug);
    setForm({
      state_slug: a.state_slug, state_code: a.state_code, state_name: a.state_name,
      tier: a.tier || "primary", order: a.order || 100,
      coverage_summary: a.coverage_summary || "",
      counties: a.counties || [], featured_services: a.featured_services || [],
      published: a.published !== false,
    });
    setShowForm(true);
  }

  function startNew() {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  }

  async function save() {
    try {
      if (editing) {
        const r = await api.patch(`/admin/website/service-areas/${editing}`, form);
        setAreas((prev) => prev.map((a) => a.state_slug === editing ? r.data : a));
        toast.success(`${r.data.state_name} updated`);
      } else {
        const r = await api.post("/admin/website/service-areas", form);
        setAreas((prev) => [...prev, r.data].sort((a, b) => (a.order || 0) - (b.order || 0)));
        toast.success(`${r.data.state_name} added`);
      }
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function remove(slug) {
    if (!window.confirm(`Delete service area "${slug}"?`)) return;
    try {
      await api.delete(`/admin/website/service-areas/${slug}`);
      setAreas((prev) => prev.filter((a) => a.state_slug !== slug));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="max-w-3xl" data-testid="website-service-areas-tab">
      <div className="flex justify-between mb-3">
        <div className="text-sm font-nav text-erf-dark/70">
          <MapPin className="w-3.5 h-3.5 inline mr-1 text-erf-goldHover" />
          {areas.length} service {areas.length === 1 ? "area" : "areas"} on the public site. Adding or removing a primary state auto-updates the corridor copy across the home page, contact page, and JSON-LD.
        </div>
        <Button size="sm" onClick={startNew} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="service-area-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Add state
        </Button>
      </div>

      <div className="grid gap-2">
        {areas.map((a) => (
          <Card key={a.state_slug} className="border-erf-borderCream" data-testid={`service-area-row-${a.state_slug}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">{a.tier}</span>
                  <span className="text-[10px] font-mono text-erf-dark/50">order {a.order}</span>
                  {!a.published && <span className="text-[10px] font-mono uppercase text-red-700 bg-red-50 border border-red-200 px-1.5 rounded-sm">Unpublished</span>}
                </div>
                <div className="font-display font-semibold text-erf-dark">{a.state_name} ({a.state_code})</div>
                <div className="text-xs font-nav text-erf-dark/60 mt-1 line-clamp-2">{a.coverage_summary}</div>
                {(a.counties || []).length > 0 && (
                  <div className="text-[11px] font-mono text-erf-dark/50 mt-1">
                    Counties: {a.counties.join(", ")}
                  </div>
                )}
                <div className="mt-2">
                  <CopyLinkControl defaultRef={a.state_slug} destinationPath="/quote" testIdPrefix={`sa-copy-${a.state_slug}`} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(a)} className="border-erf-borderCream" data-testid={`service-area-edit-${a.state_slug}`}>
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(a.state_slug)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`service-area-delete-${a.state_slug}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl bg-erf-cream" data-testid="service-area-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "Add"} service area</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">State slug (URL)</Label>
                <Input value={form.state_slug} onChange={(e) => setForm({ ...form, state_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} disabled={!!editing} placeholder="pennsylvania" data-testid="service-area-slug" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Code</Label>
                <Input maxLength={2} value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value.toUpperCase() })} placeholder="PA" data-testid="service-area-code" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} data-testid="service-area-order" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Full state name</Label>
              <Input value={form.state_name} onChange={(e) => setForm({ ...form, state_name: e.target.value })} placeholder="Pennsylvania" data-testid="service-area-name" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Tier</Label>
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="w-full h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white" data-testid="service-area-tier">
                <option value="primary">Primary (part of corridor)</option>
                <option value="secondary">Secondary (available)</option>
                <option value="on_request">On request only</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Coverage summary</Label>
              <textarea rows={3} value={form.coverage_summary} onChange={(e) => setForm({ ...form, coverage_summary: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav"
                data-testid="service-area-summary" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Counties (comma-separated, opt.)</Label>
              <Input value={(form.counties || []).join(", ")}
                onChange={(e) => setForm({ ...form, counties: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                data-testid="service-area-counties" />
            </div>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="service-area-published" />
              Published on public site
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="service-area-save">
                <Save className="w-3.5 h-3.5 mr-1" /> {editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Wave 3.2b — Conversion analytics tab (audit-ledger derived) ────────
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  function load() {
    api.get("/admin/website/analytics", { params: { days } }).then((r) => setData(r.data)).catch(() => setData(null));
  }

  return (
    <div className="max-w-3xl grid gap-3" data-testid="website-analytics-tab">
      <Card className="border-erf-borderCream">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
                <BarChart3 className="w-3 h-3 inline mr-1" /> Conversion analytics
              </div>
              <div className="font-display font-semibold text-erf-dark text-2xl mt-1">
                {data ? data.total_submissions : "—"} quote {data?.total_submissions === 1 ? "submission" : "submissions"}
              </div>
              <div className="text-xs font-nav text-erf-dark/60 mt-1">
                Derived from the immutable audit ledger — every submission is a real event, never fabricated.
              </div>
            </div>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white" data-testid="analytics-window">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last 365 days</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {!data || data.total_submissions === 0 ? (
        <Card className="border-erf-borderCream">
          <CardContent className="p-8 text-center text-erf-dark/60 font-nav text-sm">
            No quote submissions in this window yet. When a visitor submits{" "}
            <a href="/quote" className="text-erf-goldHover underline" target="_blank" rel="noreferrer">the public quote form</a>,{" "}
            an audit-ledger entry is recorded and surfaces here. Use <span className="font-mono text-xs">?ref=…</span> in campaign URLs to see per-channel attribution.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border-erf-borderCream">
            <CardContent className="p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-2">By source (ref)</div>
              <div className="grid gap-1.5">
                {data.by_ref.map((r) => (
                  <div key={r.key} className="flex items-center justify-between text-sm font-nav border-b border-erf-borderCream/60 last:border-0 py-1">
                    <span className="font-mono text-xs truncate">{r.key}</span>
                    <span className="font-mono text-xs text-erf-dark/70">{r.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-erf-borderCream">
            <CardContent className="p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-2">By service</div>
              <div className="grid gap-1.5">
                {data.by_service.map((r) => (
                  <div key={r.key} className="flex items-center justify-between text-sm font-nav border-b border-erf-borderCream/60 last:border-0 py-1">
                    <span className="font-mono text-xs truncate">{r.key}</span>
                    <span className="font-mono text-xs text-erf-dark/70">{r.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-erf-borderCream">
            <CardContent className="p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-2">By day</div>
              <div className="grid gap-1.5">
                {data.by_day.map((r) => (
                  <div key={r.date} className="flex items-center justify-between text-sm font-nav border-b border-erf-borderCream/60 last:border-0 py-1">
                    <span className="font-mono text-xs">{r.date}</span>
                    <span className="font-mono text-xs text-erf-dark/70">{r.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
      <CampaignManager />
      <DigestCard />
    </div>
  );
}

// ─── R1d.3 — Campaign Manager (attribution URL generation + live counts) ─
function CampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [seenOnly, setSeenOnly] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ref_slug: "", label: "", destination_path: "/quote" });
  const [qrTarget, setQrTarget] = useState(null);
  const [justCreated, setJustCreated] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => { load(); }, []);
  function load() {
    api.get("/admin/website/campaigns").then((r) => {
      setCampaigns(r.data.campaigns || []);
      setSeenOnly(r.data.seen_only_refs || []);
    }).catch(() => {});
  }

  async function create() {
    if (!form.ref_slug || !form.label) {
      toast.error("Ref slug and label are required.");
      return;
    }
    try {
      const r = await api.post("/admin/website/campaigns", {
        ref_slug: form.ref_slug, label: form.label,
        destination_path: form.destination_path || "/quote",
      });
      setShowForm(false);
      const url = `${origin}${r.data.destination_path}?ref=${r.data.ref_slug}`;
      setJustCreated({ url, label: r.data.label });
      setForm({ ref_slug: "", label: "", destination_path: "/quote" });
      toast.success("Campaign created");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function archive(slug) {
    if (!window.confirm(`Archive campaign "${slug}"?`)) return;
    try {
      await api.patch(`/admin/website/campaigns/${slug}`, { archived: true });
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="campaign-manager">
      <Card className="border-erf-borderCream mt-4">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
                <Megaphone className="w-3 h-3 inline mr-1" /> Campaign short links
              </div>
              <div className="font-display font-semibold text-erf-dark text-lg mt-1">
                Attribution URLs by channel
              </div>
              <div className="text-xs font-nav text-erf-dark/60 mt-1">
                Add <span className="font-mono">?ref=…</span> to any public URL. Every submission that uses the ref is recorded to the audit ledger and counted here.
              </div>
            </div>
            <Button size="sm" onClick={() => setShowForm(true)} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="campaign-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Generate new campaign URL
            </Button>
          </div>

          {campaigns.length === 0 && seenOnly.length === 0 ? (
            <div className="text-center text-erf-dark/60 font-nav text-sm py-6" data-testid="campaign-empty">
              No campaigns registered and no ref values seen in submissions yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-nav">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 border-b border-erf-borderCream">
                  <tr>
                    <th className="text-left py-2 pr-3">Label</th>
                    <th className="text-left py-2 pr-3">Ref slug</th>
                    <th className="text-left py-2 pr-3">Destination</th>
                    <th className="text-right py-2 pr-3">24h</th>
                    <th className="text-right py-2 pr-3">7d</th>
                    <th className="text-right py-2 pr-3">30d</th>
                    <th className="text-right py-2 pr-3">Total</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const url = `${origin}${c.destination_path || "/quote"}?ref=${c.ref_slug}`;
                    return (
                      <tr key={c.ref_slug} className="border-b border-erf-borderCream/60" data-testid={`campaign-row-${c.ref_slug}`}>
                        <td className="py-2 pr-3">
                          <div className="font-semibold text-erf-dark">{c.label}</div>
                          {c.archived_at && <span className="text-[10px] font-mono text-red-700">Archived</span>}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs">{c.ref_slug}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-erf-dark/70">{c.destination_path}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs">{(c.counts && c.counts["24h"]) || 0}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs">{(c.counts && c.counts["7d"]) || 0}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs">{(c.counts && c.counts["30d"]) || 0}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs font-semibold">{(c.counts && c.counts.total) || 0}</td>
                        <td className="py-2 flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(url, `Copied ${url}`)} className="h-7 px-2 border-erf-borderCream text-[11px]" data-testid={`campaign-copy-${c.ref_slug}`}>
                            <Link2 className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setQrTarget({ url, label: c.label })} className="h-7 px-2 border-erf-borderCream text-[11px]" data-testid={`campaign-qr-${c.ref_slug}`}>
                            <QrCode className="w-3 h-3" />
                          </Button>
                          {!c.archived_at && (
                            <Button size="sm" variant="outline" onClick={() => archive(c.ref_slug)} className="h-7 px-2 border-red-200 text-red-600 hover:bg-red-50 text-[11px]" data-testid={`campaign-archive-${c.ref_slug}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {seenOnly.length > 0 && (
                    <>
                      <tr><td colSpan={8} className="pt-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">Unregistered refs seen in submissions</td></tr>
                      {seenOnly.map((r) => (
                        <tr key={`seen-${r.ref_slug}`} className="border-b border-erf-borderCream/60 opacity-70" data-testid={`campaign-seen-${r.ref_slug}`}>
                          <td className="py-2 pr-3 italic text-erf-dark/60">(not registered)</td>
                          <td className="py-2 pr-3 font-mono text-xs">{r.ref_slug}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-erf-dark/70">—</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{(r.counts && r.counts["24h"]) || 0}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{(r.counts && r.counts["7d"]) || 0}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs">{(r.counts && r.counts["30d"]) || 0}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs font-semibold">{(r.counts && r.counts.total) || 0}</td>
                          <td className="py-2"></td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl bg-erf-cream" data-testid="campaign-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Generate new campaign URL</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs font-mono uppercase">Label (internal)</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="LinkedIn — October PA push"
                data-testid="campaign-form-label" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Ref slug (url-safe, unique)</Label>
              <Input value={form.ref_slug}
                onChange={(e) => setForm({ ...form, ref_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
                placeholder="linkedin-oct-pa"
                data-testid="campaign-form-ref" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Destination path</Label>
              <select value={form.destination_path} onChange={(e) => setForm({ ...form, destination_path: e.target.value })}
                className="w-full h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white"
                data-testid="campaign-form-dest">
                <option value="/quote">/quote (default — request-a-quote form)</option>
                <option value="/services">/services</option>
                <option value="/service-areas">/service-areas</option>
                <option value="/">/ (home)</option>
              </select>
            </div>
            {form.ref_slug && (
              <div className="bg-white border border-erf-borderCream rounded-md p-3 font-mono text-xs break-all">
                {origin}{form.destination_path}?ref={form.ref_slug}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={create} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="campaign-form-save">
                <Save className="w-3.5 h-3.5 mr-1" /> Create campaign
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post-create QR reveal */}
      {justCreated && (
        <QRCodeModal
          open={!!justCreated}
          onClose={() => setJustCreated(null)}
          url={justCreated.url}
          label={justCreated.label}
        />
      )}
      {/* Per-row QR modal */}
      {qrTarget && (
        <QRCodeModal
          open={!!qrTarget}
          onClose={() => setQrTarget(null)}
          url={qrTarget.url}
          label={qrTarget.label}
        />
      )}
    </div>
  );
}

// ─── R1d.4 — Weekly Digest card + recipient management ─────────────────
function DigestCard() {
  const [digest, setDigest] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [previewTab, setPreviewTab] = useState("html");
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [newRecipient, setNewRecipient] = useState({ email: "", name: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { loadAll(); }, []);
  async function loadAll() {
    try {
      const [d, r] = await Promise.all([
        api.get("/admin/website/digest/current-week"),
        api.get("/admin/website/digest/recipients"),
      ]);
      setDigest(d.data);
      setRecipients(r.data);
    } catch (e) {
      // digest endpoints may 403 for non-admin; silently skip.
    }
  }

  async function replyToTopLead(ref) {
    try {
      const r = await api.get("/leads/latest-by-ref", { params: { ref } });
      const leadId = r.data.lead?.id;
      if (!leadId) return toast.error("No lead found for that ref.");
      window.location.href = `/crm/leads/${leadId}?reply=1&template=digest`;
    } catch (e) {
      const msg = e.response?.data?.detail?.message || `No lead found yet for "${ref}".`;
      toast.error(msg);
    }
  }

  async function sendNow() {
    if (!window.confirm("Send the weekly digest to all active recipients now?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/website/digest/send-now");
      toast.success(`Queued ${r.data.outbox_rows.length} outbox row(s) for week of ${r.data.week_of}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  }

  async function addRecipient() {
    if (!newRecipient.email) return toast.error("Email required.");
    try {
      await api.post("/admin/website/digest/recipients", newRecipient);
      setNewRecipient({ email: "", name: "" });
      setShowAddRecipient(false);
      toast.success("Recipient added");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function archiveRecipient(id) {
    if (id.startsWith("owner:")) return toast.message("Owner-role recipient is implicit and cannot be archived.");
    if (!window.confirm("Remove this digest recipient?")) return;
    try {
      await api.patch(`/admin/website/digest/recipients/${id}/archive`);
      toast.success("Recipient removed");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  if (!digest) return null;
  const delta = digest.delta_pct;
  const deltaStr = delta === null ? "—" : (delta >= 0 ? `+${delta}%` : `${delta}%`);
  const active = recipients.filter((r) => !r.archived_at);
  return (
    <Card className="border-erf-borderCream mt-4" data-testid="digest-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
              <Mail className="w-3 h-3 inline mr-1" /> This week's channel digest
            </div>
            <div className="font-display font-semibold text-erf-dark text-lg mt-1">
              Week of {digest.week_of}
            </div>
            <div className="text-xs font-nav text-erf-dark/60 mt-1">
              {digest.this_week_total} submissions this week <span className={delta === null ? "text-erf-dark/50" : (delta >= 0 ? "text-emerald-700" : "text-red-700")}>({deltaStr} vs last week)</span> · Sent every Monday 07:00 UTC to {active.length} recipient{active.length === 1 ? "" : "s"}.
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={loadAll} className="border-erf-borderCream" data-testid="digest-refresh">
              Regenerate now
            </Button>
            <Button size="sm" onClick={sendNow} disabled={sending} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="digest-send-now">
              <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "Sending…" : "Send now"}
            </Button>
          </div>
        </div>

        {digest.empty_state ? (
          <div className="bg-white border border-dashed border-erf-borderCream rounded-md p-4 text-sm font-nav text-erf-dark/70" data-testid="digest-empty">
            No public quote submissions this week. When submissions arrive, the digest will populate automatically — every count traces back to a real audit-ledger event.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <div className="bg-white border border-erf-borderCream rounded-md p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-2">Top 3 channels</div>
              {digest.top_channels.map((c) => (
                <div key={c.ref} className="flex items-center justify-between text-sm font-nav border-b border-erf-borderCream/60 last:border-0 py-1" data-testid={`digest-top-ref-${c.ref}`}>
                  <span><strong className="text-erf-goldHover mr-2">#{c.rank}</strong><span className="font-mono text-xs">{c.ref}</span></span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-xs">{c.count}{c.week_over_week_pct !== null ? ` (${c.week_over_week_pct >= 0 ? "+" : ""}${c.week_over_week_pct}%)` : ""}</span>
                    <Button
                      size="sm" variant="outline"
                      className="h-6 px-1.5 text-[10px] border-erf-borderCream"
                      title={`Reply to the top lead from "${c.ref}"`}
                      onClick={() => replyToTopLead(c.ref)}
                      data-testid={`digest-reply-top-${c.ref}`}
                    >
                      <MessageSquare className="w-3 h-3 mr-1" /> Reply
                    </Button>
                  </span>
                </div>
              ))}
            </div>
            <div className="bg-white border border-erf-borderCream rounded-md p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-2">Top states</div>
              {digest.top_states.length === 0 ? (
                <div className="text-xs font-nav text-erf-dark/50 italic">No state data this week.</div>
              ) : digest.top_states.map((s) => (
                <div key={s.state} className="flex items-center justify-between text-sm font-nav border-b border-erf-borderCream/60 last:border-0 py-1">
                  <span className="font-semibold">{s.state}</span>
                  <span className="font-mono text-xs">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 bg-white border border-erf-borderCream rounded-md p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Recommended actions</div>
          <ul className="text-sm font-nav text-erf-dark/80 list-disc list-inside space-y-1">
            {digest.recommendations.map((r, i) => (<li key={i}>{r}</li>))}
          </ul>
        </div>

        {/* Preview tabs */}
        <div className="mt-4">
          <div className="flex gap-1 mb-2">
            <button className={`px-2 py-1 text-[11px] font-mono uppercase tracking-widest rounded-sm ${previewTab === "html" ? "bg-erf-dark text-erf-cream" : "bg-erf-creamSecondary text-erf-dark/70"}`} onClick={() => setPreviewTab("html")} data-testid="digest-tab-html">HTML preview</button>
            <button className={`px-2 py-1 text-[11px] font-mono uppercase tracking-widest rounded-sm ${previewTab === "text" ? "bg-erf-dark text-erf-cream" : "bg-erf-creamSecondary text-erf-dark/70"}`} onClick={() => setPreviewTab("text")} data-testid="digest-tab-text">Plain text</button>
          </div>
          {previewTab === "html" ? (
            <iframe
              title="Digest HTML preview"
              srcDoc={digest.preview_html}
              className="w-full h-[420px] border border-erf-borderCream rounded-md bg-white"
              data-testid="digest-preview-html"
            />
          ) : (
            <pre className="w-full max-h-[420px] overflow-y-auto p-3 bg-white border border-erf-borderCream rounded-md font-mono text-[11px] whitespace-pre-wrap" data-testid="digest-preview-text">
              {digest.preview_text}
            </pre>
          )}
        </div>

        {/* Recipients */}
        <div className="mt-4 bg-white border border-erf-borderCream rounded-md p-3" data-testid="digest-recipients">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
              <UserPlus className="w-3 h-3 inline mr-1" /> Digest recipients ({active.length})
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowAddRecipient(true)} className="h-7 px-2 border-erf-borderCream text-[11px]" data-testid="digest-add-recipient">
              <Plus className="w-3 h-3 mr-1" /> Add recipient
            </Button>
          </div>
          {active.length === 0 ? (
            <div className="text-xs font-nav text-erf-dark/50 italic">No recipients configured — digest will not send.</div>
          ) : (
            <ul className="text-sm font-nav divide-y divide-erf-borderCream/60">
              {active.map((r) => (
                <li key={r.id} className="py-1 flex items-center justify-between" data-testid={`digest-recipient-${r.id}`}>
                  <span>
                    <strong className="text-erf-dark">{r.name || r.email}</strong>
                    <span className="ml-2 text-erf-dark/60 text-xs font-mono">{r.email}</span>
                    {r.implicit_owner && <span className="ml-2 text-[10px] font-mono uppercase text-erf-goldHover">Owner default</span>}
                  </span>
                  {!r.implicit_owner && (
                    <Button size="sm" variant="outline" onClick={() => archiveRecipient(r.id)} className="h-6 px-1 border-red-200 text-red-600 hover:bg-red-50" data-testid={`digest-recipient-archive-${r.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>

      <Dialog open={showAddRecipient} onOpenChange={setShowAddRecipient}>
        <DialogContent className="max-w-md bg-erf-cream" data-testid="digest-recipient-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add digest recipient</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs font-mono uppercase">Email</Label>
              <Input value={newRecipient.email} onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })} placeholder="admin@erf-enterprises.com" data-testid="digest-recipient-email" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Name (optional)</Label>
              <Input value={newRecipient.name} onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })} data-testid="digest-recipient-name" />
            </div>
            <div className="text-[11px] font-nav text-erf-dark/60">
              Only users with Owner or Administrator roles in this tenant can be added.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddRecipient(false)}>Cancel</Button>
              <Button onClick={addRecipient} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="digest-recipient-save">
                <Save className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Wave 3.2b — Release notes tab (static, versioned) ──────────────────
function ReleaseNotesTab() {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get("/admin/website/release-notes").then((r) => setNotes(r.data)).catch(() => {});
  }, []);
  return (
    <div className="max-w-3xl grid gap-3" data-testid="website-release-notes-tab">
      {notes.map((n) => (
        <Card key={n.version} className="border-erf-borderCream" data-testid={`release-note-${n.version}`}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-erf-goldHover" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">{n.version}</span>
              <span className="text-[10px] font-mono text-erf-dark/50">{n.date}</span>
            </div>
            <div className="font-display font-semibold text-erf-dark text-lg">{n.title}</div>
            <ul className="mt-2 text-sm font-nav text-erf-dark/80 space-y-1 list-disc list-inside">
              {n.highlights.map((h, i) => (<li key={i}>{h}</li>))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Wave 3.2b — First-run product tour ─────────────────────────────────
const TOUR_STEPS = [
  {
    title: "Welcome to Website Content",
    body: "This is where you manage everything visitors see on the ERF public site. Every change is audit-logged and pushes live immediately.",
  },
  {
    title: "Content tab",
    body: "Edit hero headlines, mission, value props, and footer copy. Each field saves independently. Corridor-related copy (mentions of state coverage) is highlighted on the public site.",
  },
  {
    title: "Service Areas",
    body: "Add, edit, or remove the states you cover. Primary-tier states auto-update the corridor copy on the home page, contact page, and JSON-LD structured data.",
  },
  {
    title: "Analytics + Campaign short links",
    body: "Real conversion data from public quote submissions — pulled from the immutable audit ledger, never fabricated. Register campaign short-links (e.g. /quote?ref=linkedin-oct-pa) to see per-channel attribution. Add ?ref=… to any public URL and it will show up in the campaign table with live counts.",
  },
];

function TourDialog({ open, step, onNext, onComplete, onDismiss }) {
  const s = TOUR_STEPS[Math.min(step, TOUR_STEPS.length - 1)] || TOUR_STEPS[0];
  const isLast = step >= TOUR_STEPS.length - 1;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDismiss()}>
      <DialogContent className="max-w-md bg-erf-cream" data-testid="website-tour-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-erf-goldHover" /> {s.title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm font-nav text-erf-dark/80">{s.body}</p>
        <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">
          Step {step + 1} of {TOUR_STEPS.length}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onDismiss} data-testid="tour-dismiss">Skip tour</Button>
          {isLast ? (
            <Button onClick={onComplete} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="tour-complete">
              Got it
            </Button>
          ) : (
            <Button onClick={() => onNext(step + 1)} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="tour-next">
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

